<template>
  <v-card :loading="loading">
    <v-card-title class="subtitle-2">
      Playtime by tension beats (sessions more than 20 minutes)
    </v-card-title>
    <v-card-text v-if="!show || loading" height="350" class="pa-2 caption black--text">
      <v-sheet height="350" class="d-flex justify-center align-center">
        <v-btn
          color="primary"
          :loading="loading"
          @click="show = true"
        >
          Load data
        </v-btn>
      </v-sheet>
    </v-card-text>
    <template v-else>
      <v-card-text class="pa-2 caption black--text">
        <v-select
          v-model="selectedMap"
          :items="maps"
          label="Select map"
          hide-details
          outlined
          dense
          @change="updateSelectedEncounters"
        />
        <apexchart
          :key="chartKey"
          :height="350"
          :options="chartOptions"
          :series="filteredTensionBeatsSeries"
        />
      </v-card-text>
      <v-card-text class="pa-2 caption black--text">
        <v-select
          v-model="selectedDataCategory"
          :items="['Min', 'Avg', 'Max']"
          label="Select data category"
          hide-details
          outlined
          dense
        />
        <apexchart
          :key="selectedDataCategory"
          :height="350"
          :options="chartOptionsForTimeline"
          :series="filteredMapSeries"
        />
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
import helper from '../../plugins/helpers/mixin-helper'
export default {
  mixins: [
    helper
  ],
  data () {
    return {
      loading: false,
      show: false,
      selectedMap: "All",
      selectedDataCategory: 'Avg',
      encounters: [],
      selectedEncounters: [],
      maps: [],
      tensionBeatsSeries: [
        {
          data: []
        }
      ],
      mapSeries: [],
      chartOptions: {
        chart: {
          type: 'scatter',
          stacked: false,
          animations: {
            enabled: false
          }
        },
        dataLabels: {
          enabled: false,
          formatter: (value) => this.timeToString(value),
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ['#304758'],
          },
        },
        stroke: {
          width: 1,
          colors: ['#fff']
        },
        xaxis: {
          categories: [],
        },
        yaxis: {
          labels: {
            formatter: (value) => this.timeToString(value)
          }
        },
        tooltip: {
          y: {
            formatter: (value) => this.timeToString(value)
          }
        },
        fill: {
          opacity: 1
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          offsetX: 40
        }
      },
      chartOptionsForTimeline: {
        chart: {
          height: 350,
          type: 'rangeBar',
        },
        plotOptions: {
          bar: {
            horizontal: true,
            barHeight: '50%',
            rangeBarGroupRows: true,
          },
        },
        fill: {
          type: 'solid',
        },
        xaxis: {
          type: 'datetime',
        },
        legend: {
          show: false,
        },
        xaxis: {
          labels: {
            formatter: (value) => this.timeToString(value)
          }
        },
        tooltip: {
          custom: (opts) => {
            const fromTime = this.timeToString(opts.y1)
            const toTime = this.timeToString(opts.y2)

            const w = opts.ctx.w
            let ylabel = ''
            if (
              w.config.series[opts.seriesIndex].data &&
              w.config.series[opts.seriesIndex].data[opts.dataPointIndex]
            ) {
              ylabel = w.config.series[opts.seriesIndex].data[opts.dataPointIndex].x
            }
            let seriesName = w.config.series[opts.seriesIndex].name
              ? w.config.series[opts.seriesIndex].name
              : ''
            const color = w.globals.colors[opts.seriesIndex]

            return (
              '<div class="apexcharts-tooltip-rangebar">' +
              '<div> <span class="series-name" style="color: ' +
              color +
              '">' +
              (seriesName ? seriesName : '') +
              '</span></div>' +
              '<div> <span class="category">' +
              ylabel +
              ' </span> <span class="value start-value">' +
              fromTime +
              '</span> <span class="separator">-</span> <span class="value end-value">' +
              toTime +
              '</span></div>' +
              '</div>'
            )
          },
        }
      }
    }
  },
  watch: {
    async '$route.params' () {
      if (this.show) {
        await this.getData()
      }
    },
    async show (val) {
      if (val) {
        await Promise.all([
          this.getData(),
          this.getEncounters()
        ])
      }
    }
  },
  computed: {
    filteredTensionBeatsSeries () {
      if (!this.selectedEncounters.length) {
        return this.tensionBeatsSeries
      }

      const selected = this.selectedEncounters.map(String)

      return this.tensionBeatsSeries.map(series => ({
        ...series,
        data: series.data.filter(item =>
          selected.includes(String(item.x))
        )
      }))
    },
    filteredMapSeries () {
      return this.mapSeries[this.selectedDataCategory.toLocaleLowerCase()]
    },
    chartKey () {
      return this.selectedEncounters
        .map(String)
        .sort()
        .join('|')
    },
  },
  methods: {
    async getEncounters () {
      const { data } = await this.$axios.get(
        `${this.$store.getters.projectPath}/configs/telemetry/encounters`
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.encounters = data
        this.selectedEncounters = data.map(encounter => encounter.name)
        this.maps = Array.from(new Set([ 'All', ...data.map(map => map.map_name) ]))
      }
    },
    updateSelectedEncounters (val) {
      if (val === "All") {
        this.selectedEncounters = this.encounters.map(encounter => encounter.name)
      } else {
        this.selectedEncounters = this.encounters
          .filter(encounter => encounter.map_name === val)
          .map(encounter => encounter.name)
      }
    },
    async getData () {
      this.loading = true
      const { data } = await this.$axios.get(
        `${this.$store.getters.projectPath}/marketing/telemetry/playtime-by-tension-beats`, {
          params: {
            build_tag: this.$route.params.buildTag
          }
        }
      ).catch(error => ({ data: { err: true, message: error.response.data } }))

      if (data.err) {
        this.$toasted.error(data.message)
      } else {
        this.tensionBeatsSeries = data.tensionBeatsSeries
        this.mapSeries = data.mapSeries
      }
      this.loading = false
    }
  }
}
</script>

<style scoped>
  .align-top {
    vertical-align: top;
  }
</style>